import {
  AccountType,
  AircraftRestriction,
  AircraftType,
  BacklogStatus,
  BatteryType,
  BatteryTypeVariant,
  BusinessType,
  CancelReason,
  CargoCooling,
  CargoCoolingRange,
  CargoType,
  ClarificationReason,
  Contact,
  CostUnitType,
  DataType,
  DimensionUnit,
  DocumentType,
  EarliestTenderOption,
  EstimatedTransitTime,
  ExpiryReason,
  FreightService,
  FreightServiceAddOn,
  FreightStatus,
  HandlingOptions,
  InlandCategory,
  MarkupCategory,
  NotificationCategory,
  OnboardingStep,
  PackingGroup,
  PackingInstruction,
  QuoteStatus,
  RateCategory,
  RateImportState,
  RequestStatus,
  Section,
  ServiceType,
  SurchargeCategory,
  SurchargeUnit,
  SurchargeWeightType,
  TimeOfDay,
  TrackingShipmentStatus,
  UnNumber,
  VariantType,
  WeightType,
  WeightUnit,
} from '@pelicargo/types';
import {
  getPrettyPaymentStatus,
  permissionHierarchy,
  permissionHierarchyOrder,
  PermissionLevel,
} from '@pelicargo/utils';
import {
  CargoTypeSub,
  IATAFlightSegmentStatus,
  IdentitySystem,
  InstructionUsage,
  LineItemAudience,
  LineItemType,
  MarkupConfigurationType,
  PaymentStatus,
  ShipmentFormPayment,
  ShipmentStatus,
} from '@prisma/client';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';

import { isInlandCategory, isMarkupCategory, isRateCategory, isSurchargeCategory } from '../models/line-item';
import {
  getPrettyAccountType,
  getPrettyAircraftRestriction,
  getPrettyAircraftType,
  getPrettyBacklogStatus,
  getPrettyBatteryType,
  getPrettyBatteryTypeVariant,
  getPrettyBusinessType,
  getPrettyCancelReason,
  getPrettyCargoCooling,
  getPrettyCargoTempRange,
  getPrettyCargoType,
  getPrettyCargoTypeSub,
  getPrettyClarificationReason,
  getPrettyConfirmation,
  getPrettyCostUnitType,
  getPrettyDataType,
  getPrettyDimensionUnit,
  getPrettyDocumentType,
  getPrettyEarliestTenderOption,
  getPrettyEstimatedTransitTime,
  getPrettyExpiryReason,
  getPrettyFreightService,
  getPrettyFreightServiceAddOn,
  getPrettyFreightStatus,
  getPrettyHandling,
  getPrettyIATAFlightSegmentStatus,
  getPrettyIdentitySystem,
  getPrettyInlandCategory,
  getPrettyInstructionUsage,
  getPrettyLineItemAudience,
  getPrettyLineItemType,
  getPrettyMarkupCategory,
  getPrettyMarkupConfigurationType,
  getPrettyNotificationCategory,
  getPrettyOnboardingStep,
  getPrettyPackingGroup,
  getPrettyPackingInstruction,
  getPrettyPermissionLevel,
  getPrettyQuoteStatus,
  getPrettyRateCategory,
  getPrettyRateImportState,
  getPrettyRequestStatus,
  getPrettySection,
  getPrettyServiceType,
  getPrettyShipmentFormPayment,
  getPrettyShipmentStatus,
  getPrettySurchargeCategory,
  getPrettySurchargeUnit,
  getPrettySurchargeWeightType,
  getPrettyTimeOfDay,
  getPrettyTrackingShipmentStatus,
  getPrettyUnNumber,
  getPrettyVariantType,
  getPrettyWeightType,
  getPrettyWeightUnit,
} from './enums';

export type PrettyOptions<T> = {
  label: string;
  value: T;
  disabled?: boolean;
};

type OptGroupOptions<T> = {
  groupLabel: string;
  options: PrettyOptions<T>[];
};

const getPrettyOptions = <T>(enumValues: T[], getLabel: (value: T) => string): PrettyOptions<T>[] => {
  return enumValues.map((value) => ({
    label: getLabel(value),
    value,
  }));
};

const getPrettyOptGroupOptions = <T>(
  enumValues: { [key: string]: T[] },
  getLabel: (value: T) => string,
): OptGroupOptions<T>[] => {
  return Object.entries(enumValues).map(([groupLabel, values]) => ({
    groupLabel,
    options: values.map((value) => ({
      label: getLabel(value),
      value,
    })),
  }));
};

// Actual Options
export const countryCodeOptions = getPrettyOptions(
  getCountries(),
  (countryCode) => `${countryCode} (+${getCountryCallingCode(countryCode)})`,
);

export const handlingOptions = getPrettyOptions(Object.values(HandlingOptions), getPrettyHandling);

export const allCargoTypeOptions = getPrettyOptions(Object.values(CargoType), getPrettyCargoType);

export const dataTypeOptions = getPrettyOptions(Object.values(DataType), getPrettyDataType);

export const identitySystemOptions = getPrettyOptions(Object.values(IdentitySystem), getPrettyIdentitySystem);

export const adminCargoTypeOptions = getPrettyOptions(
  [CargoType.GeneralCargo, CargoType.DangerousGoods, CargoType.Perishable, CargoType.Pharma, CargoType.Valuable],
  getPrettyCargoType,
);
export const adminCargoTypeSubOptions = getPrettyOptions(Object.values(CargoTypeSub), getPrettyCargoTypeSub);

export const customerCargoTypeOptions = getPrettyOptions(
  [CargoType.GeneralCargo, CargoType.DangerousGoods, CargoType.Batteries, CargoType.Perishable],
  getPrettyCargoType,
);
export const customerSubCargoTypeOptions = getPrettyOptions(
  [CargoTypeSub.GENERAL, CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA],
  getPrettyCargoTypeSub,
);

export const cargoCoolingOptions = getPrettyOptions(Object.values(CargoCooling), getPrettyCargoCooling);

export const cargoCoolingRangeOptions = getPrettyOptions(Object.values(CargoCoolingRange), getPrettyCargoTempRange);

export const aircraftRestrictionOptions = getPrettyOptions(
  Object.values(AircraftRestriction),
  getPrettyAircraftRestriction,
);

export const surchargeUnitOptions = getPrettyOptions(Object.values(SurchargeUnit), getPrettySurchargeUnit);

export const costUnitTypeOptions = getPrettyOptions(Object.values(CostUnitType), getPrettyCostUnitType);

export const surchargeWeightTypeOptions = getPrettyOptions(
  Object.values(SurchargeWeightType),
  getPrettySurchargeWeightType,
);

export const serviceTypeOptions = getPrettyOptions(Object.values(ServiceType), getPrettyServiceType);

export const variantTypeOptions = getPrettyOptions(Object.values(VariantType), getPrettyVariantType);

export const surchargeCategoryOptions = getPrettyOptions(Object.values(SurchargeCategory), getPrettySurchargeCategory);

export const backlogStatusOptions = getPrettyOptions(Object.values(BacklogStatus), getPrettyBacklogStatus);

export const estimatedTransitTimeOptions = getPrettyOptions(
  Object.values(EstimatedTransitTime),
  getPrettyEstimatedTransitTime,
);

export const quoteStatusOptions = getPrettyOptions(Object.values(QuoteStatus), getPrettyQuoteStatus);

export const requestStatusOptions = getPrettyOptions(Object.values(RequestStatus), getPrettyRequestStatus);

export const shipmentStatusOptions = getPrettyOptions(Object.values(ShipmentStatus), getPrettyShipmentStatus);

export const paymentStatusOptions = getPrettyOptions(Object.values(PaymentStatus), getPrettyPaymentStatus);

export const weightUnitOptions = getPrettyOptions(Object.values(WeightUnit), getPrettyWeightUnit);

export const weightTypeOptions = getPrettyOptions(Object.values(WeightType), getPrettyWeightType);

export const dimensionUnitOptions = getPrettyOptions(Object.values(DimensionUnit), getPrettyDimensionUnit);

export const aircraftTypeOptions = getPrettyOptions(Object.values(AircraftType), getPrettyAircraftType);

export const instructionUsageOptions = getPrettyOptions(Object.values(InstructionUsage), getPrettyInstructionUsage);

export const freightStatusOptions = getPrettyOptions(Object.values(FreightStatus), getPrettyFreightStatus);

export const expiryReasonOptions = getPrettyOptions(Object.values(ExpiryReason), getPrettyExpiryReason);

export const packingGroupOptions = getPrettyOptions(Object.values(PackingGroup), getPrettyPackingGroup);

export const packingInstructionOptions = getPrettyOptions(
  Object.values(PackingInstruction),
  getPrettyPackingInstruction,
);

export const unNumberOptions = getPrettyOptions(Object.values(UnNumber), getPrettyUnNumber);

export const sectionOptions = getPrettyOptions(Object.values(Section), getPrettySection);

export const batteryTypeOptions = getPrettyOptions(Object.values(BatteryType), getPrettyBatteryType);

export const batteryTypeVariantOptions = getPrettyOptions(
  Object.values(BatteryTypeVariant),
  getPrettyBatteryTypeVariant,
);

export const documentTypeOptions = getPrettyOptions(Object.values(DocumentType), getPrettyDocumentType);

export const accountTypeOptions = getPrettyOptions(Object.values(AccountType), getPrettyAccountType);

export const businessTypeOptions = getPrettyOptions(Object.values(BusinessType), getPrettyBusinessType);

export const trackingShipmentStatusOptions = getPrettyOptions(
  Object.values(TrackingShipmentStatus),
  getPrettyTrackingShipmentStatus,
);

export const rateImportStateOptions = getPrettyOptions(Object.values(RateImportState), getPrettyRateImportState);

export const timeOfDayOptions = getPrettyOptions(Object.values(TimeOfDay), getPrettyTimeOfDay);

export const earliestTenderOptions = getPrettyOptions(
  Object.values(EarliestTenderOption),
  getPrettyEarliestTenderOption,
);

export const onboardingStepOptions = getPrettyOptions(Object.values(OnboardingStep), getPrettyOnboardingStep);

export const clarificationReasonOptions = getPrettyOptions(
  Object.values(ClarificationReason),
  getPrettyClarificationReason,
);

export const confirmationOptions = getPrettyOptions([true, false], getPrettyConfirmation);

export const cancelReasonOptions = getPrettyOptions(Object.values(CancelReason), getPrettyCancelReason);

export const markupCategoryOptions = getPrettyOptions(Object.values(MarkupCategory), getPrettyMarkupCategory);

export const freightServiceOptions = getPrettyOptions(Object.values(FreightService), getPrettyFreightService);

export const lineItemAudienceOptions = getPrettyOptions(Object.values(LineItemAudience), getPrettyLineItemAudience);

export const freightServiceAddOnOptions = getPrettyOptions(
  Object.values(FreightServiceAddOn),
  getPrettyFreightServiceAddOn,
);

export const markupConfigurationTypeOptions = getPrettyOptions(
  Object.values(MarkupConfigurationType),
  getPrettyMarkupConfigurationType,
);

// `MarkupConfiguration.trucking_providers` is a bare `String[]` in the database, so this list
// is the only shared definition of what's valid. Kept in sync with the `Provider` enum in
// `api/src/lib/trucking/types.ts`, which asserts against it at compile time.
export const TRUCKING_PROVIDERS = ['Echo', 'NextGen', 'ForwardAir', 'GlobalTranz', 'Primo', 'SevenL'] as const;

export type TruckingProvider = (typeof TRUCKING_PROVIDERS)[number];

export const truckingProviderOptions = getPrettyOptions([...TRUCKING_PROVIDERS], (value) => value);

// Option Groups

const lineItemTypes = {
  Rate: {
    values: Object.values(RateCategory),
    labelProvider: getPrettyRateCategory,
  },
  Surcharge: {
    values: Object.values(SurchargeCategory),
    labelProvider: getPrettySurchargeCategory,
  },
  Markup: {
    values: Object.values(MarkupCategory),
    labelProvider: getPrettyMarkupCategory,
  },
  Inland: {
    values: Object.values(InlandCategory),
    labelProvider: getPrettyInlandCategory,
  },
  Other: {
    values: [LineItemType.OTHER],
    labelProvider: getPrettyLineItemType,
  },
};

export const lineItemTypeOptions = getPrettyOptGroupOptions(
  Object.fromEntries(Object.entries(lineItemTypes).map(([key, { values }]) => [key, values])),
  (value: RateCategory | SurchargeCategory | LineItemType | MarkupCategory | InlandCategory) => {
    if (isRateCategory(value)) return lineItemTypes.Rate.labelProvider(value);
    if (isSurchargeCategory(value)) return lineItemTypes.Surcharge.labelProvider(value);
    if (isMarkupCategory(value)) return lineItemTypes.Markup.labelProvider(value);
    if (isInlandCategory(value)) return lineItemTypes.Inland.labelProvider(value);

    return lineItemTypes.Other.labelProvider(value as LineItemType);
  },
);

export const prettyIataFlightSegmentStatus = getPrettyOptions(
  Object.values(IATAFlightSegmentStatus),
  getPrettyIATAFlightSegmentStatus,
);

export const getContactOptions = (contacts: Contact[]) =>
  contacts.map((contact) => ({
    label: `${contact?.preferred_name} <${contact?.email}>`,
    value: String(contact.id),
  }));

export const notificationCategoryOptions = getPrettyOptions(
  Object.values(NotificationCategory).toSorted((a, b) => {
    if (a === NotificationCategory.All) return -1;
    if (b === NotificationCategory.All) return 1;
    return a.localeCompare(b);
  }),
  getPrettyNotificationCategory,
);

export const getPermissionOptions = (userRole: PermissionLevel) => {
  return getPrettyOptions(permissionHierarchyOrder, getPrettyPermissionLevel).map((option) => ({
    ...option,
    disabled: permissionHierarchy[userRole] < permissionHierarchy[option.value],
  }));
};

export const shipmentFormPaymentOptions = getPrettyOptions(
  Object.values(ShipmentFormPayment),
  getPrettyShipmentFormPayment,
);
